/**
 * ImageEnhanceTab Component
 *
 * Tab for enhancing image quality with AI-powered improvements.
 * Supports general enhancement and region-specific enhancements (face, hands, etc.)
 */

import { useState } from 'react';
import { Wand2, Loader2, Plus, Trash2, Download } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/useToast';
import { ImageUploader } from '../ImageUploader';
import { ImageModelSelector } from '../ImageModelSelector';
import { imageGenerationAPI } from '@/services/imageGenerationAPI';
import type { UpscaleVaryMethod, EnhanceRegion, EnhanceDinoPrompt, ImageResult } from '@/types';

import { handleError } from '@/utils/errorHandler';

interface MethodOption {
  value: UpscaleVaryMethod;
  label: string;
  description: string;
}

const ENHANCE_METHODS: MethodOption[] = [
  {
    value: 'Vary (Subtle)',
    label: 'Vary (Subtle)',
    description: 'Quality improvements preserving original details',
  },
  {
    value: 'Vary (Strong)',
    label: 'Vary (Strong)',
    description: 'Major changes - may significantly alter the image',
  },
  {
    value: 'Upscale (2x)',
    label: 'Upscale 2x',
    description: 'Double resolution with detail enhancement',
  },
  {
    value: 'Upscale (1.5x)',
    label: 'Upscale 1.5x',
    description: 'Increase resolution by 50%',
  },
  {
    value: 'Upscale (Fast 2x)',
    label: 'Upscale Fast 2x',
    description: 'Quick 2x upscale, less detail',
  },
];

const DINO_PROMPTS: { value: EnhanceDinoPrompt; label: string }[] = [
  { value: 'face', label: 'Face' },
  { value: 'eye', label: 'Eyes' },
  { value: 'mouth', label: 'Mouth' },
  { value: 'hair', label: 'Hair' },
  { value: 'hand', label: 'Hands' },
  { value: 'body', label: 'Body' },
];

const DEFAULT_REGION: EnhanceRegion = {
  enabled: true,
  mask_dino_prompt: 'face',
  prompt: '',
  negative_prompt: '',
  mask_model: 'sam',
};

export function ImageEnhanceTab() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [sourceImage, setSourceImage] = useState<string>('');
  const [method, setMethod] = useState<UpscaleVaryMethod>('Vary (Subtle)');
  const [model, setModel] = useState<string | undefined>();
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, _setNegativePrompt] = useState('');
  const [enableRegions, setEnableRegions] = useState(false);
  const [regions, setRegions] = useState<EnhanceRegion[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ImageResult | null>(null);

  const handleImageSelect = (base64: string) => {
    setSourceImage(base64);
    setResult(null);
  };

  const handleImageRemove = () => {
    setSourceImage('');
    setResult(null);
  };

  const handleAddRegion = () => {
    if (regions.length < 3) {
      setRegions([...regions, { ...DEFAULT_REGION }]);
    }
  };

  const handleRemoveRegion = (index: number) => {
    setRegions(regions.filter((_, i) => i !== index));
  };

  const handleUpdateRegion = (index: number, updates: Partial<EnhanceRegion>) => {
    const newRegions = [...regions];
    newRegions[index] = { ...newRegions[index], ...updates };
    setRegions(newRegions);
  };

  const handleEnhance = async () => {
    if (!sourceImage) {
      showToast(
        'error',
        t('image.enhance.errors.noImage', { defaultValue: 'Error' }),
        t('image.enhance.errors.noImageMessage', { defaultValue: 'Please upload an image first' })
      );
      return;
    }

    setIsProcessing(true);
    setResult(null);

    try {
      const response = await imageGenerationAPI.enhanceImage({
        image: sourceImage,
        prompt: prompt,
        negative_prompt: negativePrompt,
        enhance_checkbox: true,
        uov_method: method,
        enhance_regions: enableRegions ? regions : [],
        seed: -1,
        async_process: false,
        model,
      });

      // Defensive: ensure images is always an array
      const images = Array.isArray(response.data?.images) ? response.data.images : [];
      if (response.success && images.length > 0) {
        setResult(images[0]);
        showToast(
          'success',
          t('image.enhance.success.title', { defaultValue: 'Image Enhanced!' }),
          t('image.enhance.success.message', {
            defaultValue: 'Enhanced in {{time}}s',
            time: response.data?.processing_time_seconds?.toFixed(1) ?? '0',
          })
        );
      } else {
        showToast(
          'error',
          t('image.enhance.errors.failed', { defaultValue: 'Enhancement Failed' }),
          response.error || 'No result returned'
        );
      }
    } catch (error) {
      const errorMessage = handleError(error, 'ImageEnhanceTab', 'Unknown error');
      showToast('error', 'Error', errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!result?.url) return;

    const link = document.createElement('a');
    link.href = result.url.startsWith('data:') ? result.url : `data:image/png;base64,${result.url}`;
    link.download = 'enhanced-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showToast('success', t('image.enhance.downloadTitle', { defaultValue: 'Downloaded!' }), '');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start space-x-2">
        <Wand2 className="h-5 w-5 text-blue-600 mt-0.5" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('image.enhance.title', { defaultValue: 'Enhance Image' })}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('image.enhance.description', {
              defaultValue: 'Improve image quality with AI-powered enhancement',
            })}
          </p>
        </div>
      </div>

      {/* Image Upload */}
      <div>
        <label className="label-gray">
          {t('image.enhance.uploadPrompt', { defaultValue: 'Upload an image to enhance' })}
        </label>
        <ImageUploader onImageSelect={handleImageSelect} onRemove={handleImageRemove} />
      </div>

      {sourceImage && !result && (
        <>
          {/* Enhancement Method */}
          <div>
            <label className="label-gray">
              {t('image.enhance.method', { defaultValue: 'Enhancement Method' })}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {ENHANCE_METHODS.map((m) => (
                <button
                  key={m.value}
                  onClick={() => setMethod(m.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    method === m.value
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                      : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-secondary-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <div className="font-medium text-sm">{m.label}</div>
                  <div className="text-xs mt-0.5 opacity-75">{m.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Checkpoint Model Selector */}
          <ImageModelSelector
            value={model}
            onChange={setModel}
            provider="fooocus"
            disabled={isProcessing}
          />

          {/* Optional Prompt */}
          <div>
            <label className="label-gray">
              {t('image.enhance.prompt', { defaultValue: 'Optional Prompt' })}
            </label>
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t('image.enhance.promptPlaceholder', {
                defaultValue: 'Guide the enhancement (e.g., "sharper details, vibrant colors")',
              })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-secondary-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Region-Specific Enhancement */}
          <div className="border dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-secondary-800/50">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100">
                  {t('image.enhance.regionEnhance', {
                    defaultValue: 'Region-Specific Enhancement',
                  })}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {t('image.enhance.regionEnhanceDesc', {
                    defaultValue: 'Target specific areas like faces or hands (up to 3 regions)',
                  })}
                </p>
              </div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enableRegions}
                  onChange={(e) => {
                    setEnableRegions(e.target.checked);
                    if (e.target.checked && regions.length === 0) {
                      handleAddRegion();
                    }
                  }}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {t('common.enable', { defaultValue: 'Enable' })}
                </span>
              </label>
            </div>

            {enableRegions && (
              <div className="space-y-3">
                {regions.map((region, regionIndex) => (
                  <div
                    key={`region-${regionIndex}-${region.mask_dino_prompt}`}
                    className="flex items-start space-x-3 p-3 bg-white dark:bg-secondary-700 rounded-lg border dark:border-gray-600"
                  >
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center space-x-2">
                        <select
                          value={region.mask_dino_prompt}
                          onChange={(e) =>
                            handleUpdateRegion(regionIndex, {
                              mask_dino_prompt: e.target.value as EnhanceDinoPrompt,
                            })
                          }
                          className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-secondary-800 text-gray-900 dark:text-gray-100"
                        >
                          {DINO_PROMPTS.map((dp) => (
                            <option key={dp.value} value={dp.value}>
                              {dp.label}
                            </option>
                          ))}
                        </select>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          Region {regionIndex + 1}
                        </span>
                      </div>
                      <input
                        type="text"
                        value={region.prompt}
                        onChange={(e) =>
                          handleUpdateRegion(regionIndex, { prompt: e.target.value })
                        }
                        placeholder={t('image.enhance.regionPromptPlaceholder', {
                          defaultValue: 'Prompt for this region...',
                        })}
                        className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-secondary-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                    <button
                      onClick={() => handleRemoveRegion(regionIndex)}
                      className="p-1 text-red-400 hover:bg-red-500/10 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}

                {regions.length < 3 && (
                  <button
                    onClick={handleAddRegion}
                    className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                    <span>{t('image.enhance.addRegion', { defaultValue: 'Add Region' })}</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Enhance Button */}
          <button onClick={handleEnhance} disabled={isProcessing} className="btn-primary-full">
            {isProcessing ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{t('image.enhance.processing', { defaultValue: 'Enhancing...' })}</span>
              </>
            ) : (
              <>
                <Wand2 className="h-5 w-5" />
                <span>{t('image.enhance.enhance', { defaultValue: 'Enhance Image' })}</span>
              </>
            )}
          </button>
        </>
      )}

      {/* Result Display */}
      {result && (
        <div className="space-y-4">
          {/* Before/After Comparison */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('image.enhance.before', { defaultValue: 'Original' })}
              </div>
              <div className="rounded-lg overflow-hidden border dark:border-gray-600 bg-gray-100 dark:bg-secondary-800">
                <img
                  src={
                    sourceImage.startsWith('data:')
                      ? sourceImage
                      : `data:image/png;base64,${sourceImage}`
                  }
                  alt="Original"
                  className="w-full h-auto"
                />
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('image.enhance.after', { defaultValue: 'Enhanced' })}
              </div>
              <div className="rounded-lg overflow-hidden border dark:border-gray-600 bg-gray-100 dark:bg-secondary-800">
                <img
                  src={
                    result.url.startsWith('data:')
                      ? result.url
                      : `data:image/png;base64,${result.url}`
                  }
                  alt="Enhanced"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleDownload}
              className="flex-1 btn-primary flex items-center justify-center space-x-2"
            >
              <Download className="h-5 w-5" />
              <span>{t('image.enhance.download', { defaultValue: 'Download' })}</span>
            </button>
            <button
              onClick={() => setResult(null)}
              className="px-4 btn-secondary flex items-center justify-center"
            >
              <span>{t('image.enhance.tryAgain', { defaultValue: 'Enhance Again' })}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageEnhanceTab;
